import React from "react";
import { FieldErrors } from "react-hook-form";
import { Monitor, RefreshCw, Smartphone, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { BannerAspect, type CampaignType } from "@/@types/gamification";
import {
  CUSTOMER_FACING_COPY,
  FILE_MESSAGES,
  UPLOAD_COPY,
} from "../../constants/messages";
import { FileUploadButton } from "../../components/file-upload-button";
import { BannerGalleryPicker } from "../../components/banner-gallery-picker";
import type { BannerSlotState } from "../../hooks/use-banner-slot";
import { type CreateGamificationFormData } from "../../validations/gamification.validation";
import { MAX_UPLOAD_MB } from "../../constants/upload";

const IMAGE_ACCEPT = "image/*";

const DROPZONE_CLASS =
  "h-auto w-full flex-col gap-0 whitespace-normal rounded-lg border-2 border-dashed border-gray-300 bg-white font-normal text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-600";

interface BannersCardProps {
  errors: FieldErrors<CreateGamificationFormData>;
  cover: BannerSlotState;
  alternative: BannerSlotState;
  campaignType?: CampaignType;
}

interface BannerUploadProps {
  id: string;
  label: string;
  icon: React.ReactNode;
  aspect: BannerAspect;
  aspectRatio: string;
  maxWidth: string;
  slot: BannerSlotState;
  campaignType?: CampaignType;
  error?: string;
}

const BannerUpload: React.FC<BannerUploadProps> = ({
  id,
  label,
  icon,
  aspect,
  aspectRatio,
  maxWidth,
  slot,
  campaignType,
  error,
}) => (
  <div className={`space-y-3 ${maxWidth}`}>
    <div className="flex items-center gap-2">
      {icon}
      <Label htmlFor={id}>{label}</Label>
    </div>

    {slot.preview && (
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-2 bg-gray-50">
        <div className={`relative ${aspectRatio} w-full overflow-hidden rounded-md`}>
          <img
            src={slot.preview}
            alt={`Preview ${label}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-4 right-4 flex gap-2">
          <FileUploadButton
            inputId={id}
            accept={IMAGE_ACCEPT}
            onFileChange={slot.onFileChange}
            variant="secondary"
            size="icon"
            className="pointer-events-auto h-11 w-11 shadow"
            aria-label={UPLOAD_COPY.replaceBanner(label)}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </FileUploadButton>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={slot.onRemove}
            aria-label={`Remover ${label}`}
            className="h-11 w-11 shadow"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    )}

    {!slot.preview && (
      <FileUploadButton
        inputId={id}
        accept={IMAGE_ACCEPT}
        onFileChange={slot.onFileChange}
        variant="outline"
        className={`${DROPZONE_CLASS} ${aspectRatio}`}
        aria-label={UPLOAD_COPY.chooseBanner(label)}
      >
        <Upload className="h-8 w-8 text-gray-400 mb-2" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-600">
          Clique para fazer upload
        </span>
        <span className="text-xs text-gray-500 mt-1">PNG, JPG ou WEBP</span>
        <span className="text-xs text-gray-500">Máx. {MAX_UPLOAD_MB}MB</span>
      </FileUploadButton>
    )}

    <BannerGalleryPicker
      aspect={aspect}
      slotLabel={label}
      campaignType={campaignType}
      selectedLabel={slot.galleryLabel}
      onSelect={slot.onGallerySelect}
    />

    {error && <p className="text-sm text-red-500">{error}</p>}
  </div>
);

export const BannersCard: React.FC<BannersCardProps> = ({
  errors,
  cover,
  alternative,
  campaignType,
}) => (
  <Card>
    <CardHeader>
      <CardTitle>Banners da Campanha</CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="flex flex-wrap items-start gap-8">
        <BannerUpload
          id="banner3x4"
          label={`${CUSTOMER_FACING_COPY.coverLabel} *`}
          icon={<Smartphone className="h-4 w-4 text-gray-600" />}
          aspect={BannerAspect.PORTRAIT}
          aspectRatio="aspect-[3/4]"
          maxWidth="w-full max-w-[220px]"
          slot={cover}
          campaignType={campaignType}
          error={
            errors.banner3x4
              ? String(errors.banner3x4.message || FILE_MESSAGES.banner3x4Required)
              : undefined
          }
        />

        <BannerUpload
          id="banner16x9"
          label={`${CUSTOMER_FACING_COPY.alternativeLabel} *`}
          icon={<Monitor className="h-4 w-4 text-gray-600" />}
          aspect={BannerAspect.WIDE}
          aspectRatio="aspect-[16/9]"
          maxWidth="w-full max-w-[420px]"
          slot={alternative}
          campaignType={campaignType}
          error={
            errors.banner16x9
              ? String(errors.banner16x9.message || FILE_MESSAGES.banner16x9Image)
              : undefined
          }
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          💡 {CUSTOMER_FACING_COPY.bannersHint}
        </p>
      </div>
    </CardContent>
  </Card>
);
